import React, { useState, useMemo } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import {
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';

// =============================================================================
// CONFIG - IOWA DOT Section 2317 (Revised 10/21/2025)
// =============================================================================

const PROJECT_TYPES = {
  PRIMARY: { id: 'PRIMARY', label: 'Primary and Interstate Projects' },
  NON_PRIMARY: { id: 'NON_PRIMARY', label: 'Non-Primary' },
};

const PAVEMENT_TYPES = {
  PCC: { 
    id: 'PCC', 
    label: 'PCC', 
    thicknessThreshold: 6,
    thickLabel: 'PCC>6"',
    thinLabel: 'PCC≤6"',
  },
  HMA: { 
    id: 'HMA', 
    label: 'HMA', 
    thicknessThreshold: 4,
    thickLabel: 'HMA>4"',
    thinLabel: 'HMA≤4"',
  },
};

const SPEED_TYPES = {
  HIGH: { id: 'HIGH', label: '> 45 mph' },
  LOW: { id: 'LOW', label: '> 35 mph and ≤ 45 mph' },
};

const THICKNESS_OPTIONS = {
  PCC: [
    { id: 'thick', label: 'PCC>6"', value: 7 },
    { id: 'thin', label: 'PCC≤6"', value: 6 },
  ],
  HMA: [
    { id: 'thick', label: 'HMA>4"', value: 5 },
    { id: 'thin', label: 'HMA≤4"', value: 4 },
  ],
};

// =============================================================================
// MRI PAY ADJUSTMENT TABLES (Section 2317.05)
// =============================================================================

const IOWA_MRI_TABLES = {
  PCC_PRIMARY_THICK: [
    { max: 47.5,     calc: () => 1500,                    tier: 'bonus' },
    { max: 57.5,     calc: (mri) => 8625 - (150 * mri),   tier: 'bonus' },
    { max: 75,       calc: () => 0,                       tier: 'base' },
    { max: 90,       calc: (mri) => 7500 - (100 * mri),   tier: 'penalty', grindOption: true, grindTarget: 75 },
    { max: Infinity, calc: () => 7500 - (100 * 90),       tier: 'corrective', grind: true, grindTarget: 75 },
  ],
  PCC_PRIMARY_THIN: [
    { max: 47.5,     calc: () => 1250,                    tier: 'bonus' },
    { max: 57.5,     calc: (mri) => 7187.50 - (125 * mri), tier: 'bonus' },
    { max: 75,       calc: () => 0,                       tier: 'base' },
    { max: 90,       calc: (mri) => 6250 - (83.333 * mri), tier: 'penalty', grindOption: true, grindTarget: 75 },
    { max: Infinity, calc: () => 6250 - (83.333 * 90),    tier: 'corrective', grind: true, grindTarget: 75 },
  ],
  PCC_NON_PRIMARY: [
    { max: 60,       calc: () => 300,                     tier: 'bonus' },
    { max: 70,       calc: (mri) => 2100 - (30 * mri),    tier: 'bonus' },
    { max: 80,       calc: () => 0,                       tier: 'base' },
    { max: 95,       calc: (mri) => 1600 - (20 * mri),    tier: 'penalty', grindOption: true, grindTarget: 80 },
    { max: Infinity, calc: () => 1600 - (20 * 95),        tier: 'corrective', grind: true, grindTarget: 80 },
  ],
  HMA_PRIMARY_THICK: [
    { max: 29.84,    calc: () => 1500,                        tier: 'bonus' },
    { max: 39.22,    calc: (mri) => 6271.915 - (159.915 * mri), tier: 'bonus' },
    { max: 75,       calc: () => 0,                            tier: 'base' },
    { max: 90,       calc: (mri) => 7500 - (100 * mri),        tier: 'penalty', grindOption: true, grindTarget: 75 },
    { max: Infinity, calc: () => 7500 - (100 * 90),            tier: 'corrective', grind: true, grindTarget: 75 },
  ],
  HMA_PRIMARY_THIN: [
    { max: 29.84,    calc: () => 1250,                          tier: 'bonus' },
    { max: 39.22,    calc: (mri) => 5226.596 - (133.2623 * mri), tier: 'bonus' },
    { max: 75,       calc: () => 0,                              tier: 'base' },
    { max: 90,       calc: (mri) => 6250 - (83.333 * mri),       tier: 'penalty', grindOption: true, grindTarget: 75 },
    { max: Infinity, calc: () => 6250 - (83.333 * 90),           tier: 'corrective', grind: true, grindTarget: 75 },
  ],
  HMA_NON_PRIMARY: [
    { max: 35,       calc: () => 300,                     tier: 'bonus' },
    { max: 45,       calc: (mri) => 1350 - (30 * mri),    tier: 'bonus' },
    { max: 80,       calc: () => 0,                       tier: 'base' },
    { max: 95,       calc: (mri) => 1600 - (20 * mri),    tier: 'penalty', grindOption: true, grindTarget: 80 },
    { max: Infinity, calc: () => 1600 - (20 * 95),        tier: 'corrective', grind: true, grindTarget: 80 },
  ],
};

// =============================================================================
// ALR TABLES (Tables 2317.05-1 and 2317.05-2)
// =============================================================================

const IOWA_ALR_TABLES = {
  PRIMARY: [
    { min: 0,   max: 200, penalty: 0,  tier: 'ok' },
    { min: 200, max: 250, penalty: 30, tier: 'penalty', grindOption: true, grindTarget: 200 },
    { min: 250, max: Infinity, penalty: 0, tier: 'corrective', grind: true, grindTarget: 200 },
  ],
  NON_PRIMARY_HIGH: [
    { min: 0,   max: 200, penalty: 0,  tier: 'ok' },
    { min: 200, max: 250, penalty: 15, tier: 'penalty', grindOption: true, grindTarget: 200 },
    { min: 250, max: Infinity, penalty: 0, tier: 'corrective', grind: true, grindTarget: 200 },
  ],
  NON_PRIMARY_LOW: [
    { min: 0,   max: 250, penalty: 0,  tier: 'ok' },
    { min: 250, max: 300, penalty: 15, tier: 'penalty', grindOption: true, grindTarget: 250 },
    { min: 300, max: Infinity, penalty: 0, tier: 'corrective', grind: true, grindTarget: 250 },
  ],
};

const DEFAULT_SETTINGS = {
  projectType: 'PRIMARY',
  pavementType: 'HMA',
  designThickness: 5,
  speedType: 'HIGH',
  excluded: false,
};

// =============================================================================
// CALCULATION HELPERS
// =============================================================================

const getMRITableKey = (settings) => {
  const { projectType, pavementType, designThickness } = settings;
  if (projectType === 'NON_PRIMARY') return `${pavementType}_NON_PRIMARY`;
  const thicknessThreshold = PAVEMENT_TYPES[pavementType].thicknessThreshold;
  const thickSuffix = designThickness > thicknessThreshold ? 'THICK' : 'THIN';
  return `${pavementType}_PRIMARY_${thickSuffix}`;
};

const getALRTableKey = (settings) => {
  const { projectType, speedType } = settings;
  if (projectType === 'PRIMARY') return 'PRIMARY';
  return speedType === 'HIGH' ? 'NON_PRIMARY_HIGH' : 'NON_PRIMARY_LOW';
};

const getMRIAdjustment = (mri, settings) => {
  const table = IOWA_MRI_TABLES[getMRITableKey(settings)];
  for (const tier of table) {
    if (mri <= tier.max) {
      return {
        adjustment: tier.calc(mri),
        tier: tier.tier,
        grind: tier.grind || false,
        grindOption: tier.grindOption || false,
        grindTarget: tier.grindTarget,
      };
    }
  }
  const lastTier = table[table.length - 1];
  return { adjustment: lastTier.calc(mri), tier: lastTier.tier, grind: lastTier.grind || false, grindTarget: lastTier.grindTarget };
};

const getALRAdjustment = (alrMagnitude, alrLengthFt, settings) => {
  const table = IOWA_ALR_TABLES[getALRTableKey(settings)];
  for (const tier of table) {
    if (alrMagnitude >= tier.min && alrMagnitude < tier.max) {
      return {
        adjustment: -(tier.penalty * alrLengthFt),
        penaltyPerFoot: tier.penalty,
        tier: tier.tier,
        grind: tier.grind || false,
        grindOption: tier.grindOption || false,
        grindTarget: tier.grindTarget,
      };
    }
  }
  return { adjustment: 0, penaltyPerFoot: 0, tier: 'ok', grind: false };
};

// =============================================================================
// MAIN CALCULATION
// =============================================================================

const calculate = (segments, alrDefects, settings, isReversed) => {
  if (!segments?.length) return null;
  
  // Filter out exceptions and map basic segment data
  const validSegments = segments
    .filter(seg => !seg.exception)
    .map((seg) => ({
      ...seg,
      startStation: seg.from_station_ft || 0,
      stopStation: seg.to_station_ft || 0,
    }));
  
  if (validSegments.length === 0) return null;
  
  // Sort by station value, accounting for direction
  const sortedSegments = [...validSegments].sort((a, b) => 
    isReversed 
      ? b.startStation - a.startStation  // Descending for reverse
      : a.startStation - b.startStation  // Ascending for normal
  );
  
  let totalMRIAdjustment = 0;
  let totalDistance = 0;
  let mriSum = 0;
  let correctiveCount = 0;
  
  const rows = sortedSegments.map((seg, i) => {
    const mri = seg.iri_avg_in_per_mi || 0;
    const mriResult = getMRIAdjustment(mri, settings);
    const segmentLength = Math.abs(seg.stopStation - seg.startStation);
    
    totalMRIAdjustment += mriResult.adjustment;
    totalDistance += segmentLength;
    mriSum += mri;
    if (mriResult.grind) correctiveCount++;
    
    // For reverse stationing, swap start/stop for display
    const displayStart = isReversed ? seg.stopStation : seg.startStation;
    const displayStop = isReversed ? seg.startStation : seg.stopStation;
    
    return {
      num: i + 1,
      segmentId: seg.segment_id,
      mri,
      startStation: displayStart,
      stopStation: displayStop,
      length: segmentLength,
      adjustment: mriResult.adjustment,
      tier: mriResult.tier,
      requiresGrind: mriResult.grind,
      hasIssue: mriResult.tier === 'corrective' || mriResult.tier === 'penalty',
    };
  });
  
  // Calculate ALR totals
  let alrLengthPenalty = 0;
  let alrLengthCorrective = 0;
  let totalALRDeduction = 0;
  
  (alrDefects || []).forEach(defect => {
    const magnitude = defect.magnitude || 0;
    const lengthFt = defect.length || Math.abs((defect.stopDistance || 0) - (defect.startDistance || 0));
    const alrResult = getALRAdjustment(magnitude, lengthFt, settings);
    
    if (alrResult.tier === 'penalty') {
      alrLengthPenalty += lengthFt;
      totalALRDeduction += alrResult.adjustment;
    }
    if (alrResult.tier === 'corrective') {
      alrLengthCorrective += lengthFt;
    }
  });
  
  const avgMRI = rows.length > 0 ? mriSum / rows.length : 0;
  
  return {
    rows,
    totalMRIAdjustment,
    totalALRDeduction,
    combinedTotal: totalMRIAdjustment + totalALRDeduction,
    avgMRI,
    totalDistance,
    segmentCount: rows.length,
    correctiveCount,
    alrLengthPenalty,
    alrLengthCorrective,
  };
};

// =============================================================================
// HELPERS
// =============================================================================

const cn = (...c) => c.filter(Boolean).join(' ');
const fmtStation = (ft) => {
  if (ft == null) return '—';
  const hundreds = Math.floor(ft / 100);
  const remainder = Math.round(ft % 100);
  return `${hundreds}+${String(remainder).padStart(2, '0')}`;
};
const fmtCurrency = (v) => {
  const formatted = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  return v < 0 ? `-${formatted}` : formatted;
};

// =============================================================================
// COPY VALUE COMPONENT
// =============================================================================

const CopyValue = ({ label, value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <div className={cn('flex items-center justify-between py-1.5', className)}>
      <span className="text-gray-600 text-sm">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-gray-900">{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-4 w-4 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </div>
  );
};

// =============================================================================
// COPYABLE CELL COMPONENT
// =============================================================================

const CopyableCell = ({ value, displayValue, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <td className={cn('px-3 py-2 group', className)}>
      <div className="flex items-center gap-1">
        <span>{displayValue ?? value}</span>
        <button
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
          title="Copy value"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3 text-gray-400 hover:text-gray-600" />
          )}
        </button>
      </div>
    </td>
  );
};

// =============================================================================
// COPYABLE COLUMN HEADER
// =============================================================================

const CopyableHeader = ({ label, data, className }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(data.join('\r\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  return (
    <th className={cn('px-3 py-2 text-left group', className)}>
      <span>{label}</span>
      <button
        onClick={handleCopy}
        className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity"
        title={`Copy ${label} column`}
      >
        {copied ? (
          <CheckIcon className="h-3.5 w-3.5 text-green-500 inline" />
        ) : (
          <ClipboardDocumentIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 inline" />
        )}
      </button>
    </th>
  );
};

// =============================================================================
// PAY TABLE MODAL
// =============================================================================

const PayTableModal = ({ open, onClose, settings }) => {
  const mriTable = IOWA_MRI_TABLES[getMRITableKey(settings)];
  const alrTable = IOWA_ALR_TABLES[getALRTableKey(settings)];
  const pavementType = PAVEMENT_TYPES[settings.pavementType];
  const isThick = settings.designThickness > pavementType.thicknessThreshold;
  
  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
            <DialogTitle className="text-base font-semibold text-gray-900">
              Iowa DOT Pay Adjustment Tables
            </DialogTitle>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <div className="p-4 space-y-6">
            <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm">
              <div className="font-semibold text-blue-800 mb-1">Current Configuration</div>
              <div className="text-blue-700">
                {PROJECT_TYPES[settings.projectType].label} • {pavementType.label} • 
                {settings.projectType === 'PRIMARY' ? (isThick ? ` ${pavementType.thickLabel}` : ` ${pavementType.thinLabel}`) : ''} • 
                {SPEED_TYPES[settings.speedType].label}
              </div>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">MRI Pay Adjustment — Per 0.1 Mile Segment</h3>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">MRI Range (in/mi)</th>
                    <th className="px-3 py-2 text-left border-b">Adjustment</th>
                    <th className="px-3 py-2 text-left border-b">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {mriTable.map((t, i) => {
                    const prevMax = i === 0 ? 0 : mriTable[i - 1].max;
                    const rangeLabel = t.max === Infinity ? `>${prevMax}` : `${prevMax} – ${t.max}`;
                    const sampleMRI = t.max === Infinity ? prevMax + 10 : (prevMax + t.max) / 2;
                    const adj = t.calc(sampleMRI);
                    
                    return (
                      <tr key={i} className={t.grind ? 'bg-red-50' : ''}>
                        <td className="px-3 py-2 text-gray-700">{rangeLabel}</td>
                        <td className={cn('px-3 py-2 font-mono', t.tier === 'bonus' ? 'text-green-700' : t.tier === 'penalty' || t.tier === 'corrective' ? 'text-red-700' : 'text-gray-500')}>
                          {t.tier === 'base' ? '$0.00' : fmtCurrency(adj)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-600">
                          {t.grind && <span className="text-red-600 font-semibold">Grind to ≤{t.grindTarget}</span>}
                          {t.grindOption && !t.grind && <span>Pay or grind to ≤{t.grindTarget}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">ALR Pay Adjustment — Per Foot</h3>
              <table className="w-full text-sm border border-gray-200">
                <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="px-3 py-2 text-left border-b">ALR Range (in/mi)</th>
                    <th className="px-3 py-2 text-left border-b">$/foot</th>
                    <th className="px-3 py-2 text-left border-b">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {alrTable.map((t, i) => (
                    <tr key={i} className={t.grind ? 'bg-red-50' : ''}>
                      <td className="px-3 py-2 text-gray-700">{t.max === Infinity ? `≥${t.min}` : `${t.min} – ${t.max}`}</td>
                      <td className={cn('px-3 py-2 font-mono', t.penalty > 0 || t.grind ? 'text-red-700' : 'text-gray-400')}>
                        {t.penalty > 0 ? `-$${t.penalty.toFixed(2)}` : t.grind ? 'Grind' : '—'}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">
                        {t.grind && <span className="text-red-600 font-semibold">Grind to ≤{t.grindTarget}</span>}
                        {t.grindOption && !t.grind && <span>Pay or grind to ≤{t.grindTarget}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="p-3 bg-gray-50 rounded text-xs text-gray-600">
              <p className="font-semibold mb-1">Reference</p>
              <p>Iowa DOT Standard Specifications Section 2317 — Pavement Smoothness</p>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function IowaPayAdjustment({ project, segments, alrDefects, isReversed = false }) {
  const [settings, setSettings] = useState(() => ({ 
    ...DEFAULT_SETTINGS, 
    ...project?.payAdjustment,
  }));
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [showPayTable, setShowPayTable] = useState(false);
  
  const data = useMemo(() => calculate(segments, alrDefects, settings, isReversed), [segments, alrDefects, settings, isReversed]);
  
  const displayRows = useMemo(() => {
    if (!data) return [];
    return issuesOnly ? data.rows.filter(r => r.hasIssue) : data.rows;
  }, [data, issuesOnly]);
  
  const pavementConfig = PAVEMENT_TYPES[settings.pavementType];
  const isThick = settings.designThickness > pavementConfig.thicknessThreshold;
  const thicknessOptions = THICKNESS_OPTIONS[settings.pavementType];
  const currentThicknessOption = isThick ? thicknessOptions[0] : thicknessOptions[1];
  
  // ALR threshold labels based on settings
  const isLowSpeed = settings.projectType === 'NON_PRIMARY' && settings.speedType === 'LOW';
  const alrPenaltyLabel = isLowSpeed ? '250.0 ≤ ALR < 300.0 (ft.)' : '200.0 ≤ ALR < 250.0 (ft.)';
  const alrCorrectiveLabel = isLowSpeed ? 'ALR ≥ 300.0 (ft.)' : 'ALR ≥ 250.0 (ft.)';
  
  const exportCSV = () => {
    const headers = ['Segment', 'Beginning Sta.', 'Ending Sta.', 'Segment Length (ft)', 'Final Smoothness (in/mi)', 'Segment Pay Adjustment'].join(',');
    const csvRows = displayRows.map(r => [
      r.num,
      r.startStation.toFixed(0),
      r.stopStation.toFixed(0),
      r.length.toFixed(0),
      r.mri.toFixed(2),
      r.requiresGrind ? '' : r.adjustment.toFixed(2),
    ].join(','));
    const blob = new Blob([[headers, ...csvRows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `iowa-2317-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };
  
  return (
    <div className="bg-white h-[calc(100vh-95px)] flex flex-col">
      <PayTableModal open={showPayTable} onClose={() => setShowPayTable(false)} settings={settings} />
      
      {/* Header */}
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
        <div className="mr-2">
          <h3 className="text-sm font-semibold text-gray-900">Profile Summary Sheet</h3>
          <p className="text-xs text-gray-500">Iowa DOT Section 2317</p>
        </div>
        
        <div className="h-8 w-px bg-gray-300" />
        
        {/* Route Type */}
        <Listbox value={settings.projectType} onChange={(v) => setSettings(s => ({ ...s, projectType: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {PROJECT_TYPES[settings.projectType]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(PROJECT_TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        {/* Pavement Type */}
        <Listbox value={settings.pavementType} onChange={(v) => setSettings(s => ({ ...s, pavementType: v, designThickness: THICKNESS_OPTIONS[v][0].value }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left">
              {settings.pavementType}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(PAVEMENT_TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        {/* Design Thickness */}
        {settings.projectType === 'PRIMARY' && (
          <Listbox value={currentThicknessOption.id} onChange={(v) => setSettings(s => ({ ...s, designThickness: thicknessOptions.find(o => o.id === v).value }))}>
            <div className="relative">
              <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left">
                {currentThicknessOption.label}
                <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              </ListboxButton>
              <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
                {thicknessOptions.map(t => (
                  <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer', active && 'bg-blue-50')}>
                    {t.label}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
        )}
        
        {/* Speed */}
        <Listbox value={settings.speedType} onChange={(v) => setSettings(s => ({ ...s, speedType: v }))}>
          <div className="relative">
            <ListboxButton className="h-8 pl-3 pr-7 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 text-left whitespace-nowrap">
              {SPEED_TYPES[settings.speedType]?.label}
              <ChevronUpDownIcon className="absolute right-1.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            </ListboxButton>
            <ListboxOptions className="absolute left-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
              {Object.values(SPEED_TYPES).map(t => (
                <ListboxOption key={t.id} value={t.id} className={({ active }) => cn('px-3 py-2 cursor-pointer whitespace-nowrap', active && 'bg-blue-50')}>
                  {t.label}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
        
        <div className="flex-1" />
        
        <button 
          onClick={() => setShowPayTable(true)} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <InformationCircleIcon className="h-4 w-4" />
          Pay Tables
        </button>
        
        <button 
          onClick={exportCSV} 
          className="h-8 px-3 text-sm text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
        >
          <ArrowDownTrayIcon className="h-4 w-4" />
          Export
        </button>
      </div>
      
      {/* Summary for DOT Sheet */}
      {data ? (
        <div className="px-4 py-4 border-b border-gray-200">
          <div className="grid grid-cols-2 gap-6">
            {/* Left: ALR Summary */}
            <div className="border border-gray-200 rounded">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-700">Area of Localized Roughness (ALR)</h4>
                <Listbox value={settings.excluded ? 'YES' : 'NO'} onChange={(v) => setSettings(s => ({ ...s, excluded: v === 'YES' }))}>
                  <div className="relative">
                    <ListboxButton className={cn(
                      'h-7 pl-2 pr-6 text-xs font-medium border rounded',
                      settings.excluded 
                        ? 'bg-yellow-100 border-yellow-300 text-yellow-800' 
                        : 'bg-white border-gray-300 text-gray-700'
                    )}>
                      {settings.excluded ? 'EXCLUDED' : 'NOT EXCLUDED'}
                      <ChevronUpDownIcon className="absolute right-1 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </ListboxButton>
                    <ListboxOptions className="absolute right-0 z-30 mt-1 bg-white border border-gray-200 rounded shadow-lg py-1 text-sm">
                      <ListboxOption value="NO" className={({ active }) => cn('px-3 py-1.5 cursor-pointer text-xs', active && 'bg-blue-50')}>
                        NOT EXCLUDED
                      </ListboxOption>
                      <ListboxOption value="YES" className={({ active }) => cn('px-3 py-1.5 cursor-pointer text-xs', active && 'bg-blue-50')}>
                        EXCLUDED
                      </ListboxOption>
                    </ListboxOptions>
                  </div>
                </Listbox>
              </div>
              <div className="px-3 py-2 divide-y divide-gray-100">
                <CopyValue 
                  label={alrPenaltyLabel}
                  value={settings.excluded ? '0.00' : data.alrLengthPenalty.toFixed(2)}
                />
                <CopyValue 
                  label={alrCorrectiveLabel}
                  value={settings.excluded ? '0.00' : data.alrLengthCorrective.toFixed(2)}
                />
              </div>
            </div>
            
            {/* Right: Totals */}
            {(() => {
              const correctionRequired = !settings.excluded && (data.correctiveCount > 0 || data.alrLengthCorrective > 0);
              
              return (
                <div className="border border-gray-200 rounded">
                  <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-700">Totals</h4>
                    {correctionRequired && (
                      <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                        Correction Required
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2 divide-y divide-gray-100">
                    {data.correctiveCount > 0 ? (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-gray-600 text-sm">Total Pay Adjustment</span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                            Corrective Work Needed
                          </span>
                          <span className="font-mono text-sm text-gray-400">
                            {fmtCurrency(data.totalMRIAdjustment)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <CopyValue 
                        label="Total Pay Adjustment"
                        value={data.totalMRIAdjustment.toFixed(2)}
                        displayValue={fmtCurrency(data.totalMRIAdjustment)}
                      />
                    )}
                    {correctionRequired ? (
                      <div className="flex items-center justify-between py-1.5">
                        <span className="text-gray-600 text-sm">Areas of Localized Roughness Deduction</span>
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                            Corrective Work Needed
                          </span>
                          <span className="font-mono text-sm text-gray-400">
                            {settings.excluded ? '$0.00' : fmtCurrency(data.totalALRDeduction)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <CopyValue 
                        label="Areas of Localized Roughness Deduction"
                        value={settings.excluded ? '0.00' : data.totalALRDeduction.toFixed(2)}
                        displayValue={settings.excluded ? '$0.00' : fmtCurrency(data.totalALRDeduction)}
                      />
                    )}
                    <div className="flex items-center justify-between py-1.5 font-semibold">
                      <span className="text-gray-600 text-sm">Combined Total</span>
                      {correctionRequired ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">—</span>
                          <span className="font-mono text-sm text-gray-400 font-normal">
                            {settings.excluded ? fmtCurrency(data.totalMRIAdjustment) : fmtCurrency(data.combinedTotal)}
                          </span>
                        </div>
                      ) : (
                        <span className="font-mono text-sm text-gray-900">
                          {settings.excluded ? fmtCurrency(data.totalMRIAdjustment) : fmtCurrency(data.combinedTotal)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="px-4 py-8 text-center text-gray-400 border-b border-gray-200">
          No segment data available
        </div>
      )}
      
      {/* Segments Header */}
      <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Segments {data && <span className="text-gray-400 font-normal">({data.rows.length})</span>}
        </span>
        {data && data.correctiveCount > 0 && (
          <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer text-sm">
            <input 
              type="checkbox" 
              checked={issuesOnly} 
              onChange={e => setIssuesOnly(e.target.checked)} 
              className="w-3.5 h-3.5 text-blue-600 rounded" 
            />
            Show issues only
          </label>
        )}
      </div>
      
      {/* Table */}
      <div className="flex-1 overflow-auto">
        {displayRows.length > 0 ? (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left w-12"></th>
                <th className="px-3 py-2 text-left">Beginning Sta.</th>
                <th className="px-3 py-2 text-left">Ending Sta.</th>
                <th className="px-3 py-2 text-left">Segment Length (ft)</th>
                <CopyableHeader label="Final Smoothness (in/mi)" data={displayRows.map(r => r.mri.toFixed(2))} />
                <CopyableHeader label="Segment Pay Adjustment" data={displayRows.map(r => r.requiresGrind ? '' : r.adjustment.toFixed(2))} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayRows.map(r => (
                <tr key={r.num} className={cn('hover:bg-gray-50', r.requiresGrind && 'bg-red-50')}>
                  <td className="px-3 py-2 text-gray-400 text-xs">{r.num}</td>
                  <CopyableCell 
                    value={r.startStation.toFixed(0)} 
                    displayValue={fmtStation(r.startStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.stopStation.toFixed(0)} 
                    displayValue={fmtStation(r.stopStation)}
                    className="text-gray-700 font-mono text-xs"
                  />
                  <CopyableCell 
                    value={r.length.toFixed(0)} 
                    displayValue={r.length.toFixed(0)}
                    className="text-gray-700"
                  />
                  <td className={cn('px-3 py-2 text-left', r.requiresGrind ? 'text-red-600 font-semibold' : 'text-gray-900')}>
                    {r.mri.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-left">
                    {r.requiresGrind ? (
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-xs font-semibold bg-red-500 text-white rounded">
                          Grind Required
                        </span>
                        <span className="font-mono text-gray-400 text-xs">
                          {fmtCurrency(r.adjustment)}
                        </span>
                      </div>
                    ) : (
                      <span className={cn(
                        'font-mono',
                        r.adjustment > 0 ? 'text-green-600' : r.adjustment < 0 ? 'text-red-600' : 'text-gray-500'
                      )}>
                        {fmtCurrency(r.adjustment)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-xs text-gray-500 text-right">Total Distance:</td>
                <td className="px-3 py-2 font-semibold text-gray-900">{data.totalDistance.toFixed(1)}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  <span className="mr-2">Avg. MRI:</span>
                  <span className="font-semibold text-gray-900">{data.avgMRI.toFixed(2)}</span>
                </td>
                <td className="px-3 py-2 text-xs">
                  {(data.correctiveCount > 0 || data.alrLengthCorrective > 0) && (
                    <span className="text-red-600 font-medium">
                      Corrections: {data.correctiveCount > 0 && `${data.correctiveCount} segment${data.correctiveCount !== 1 ? 's' : ''}`}
                      {data.correctiveCount > 0 && data.alrLengthCorrective > 0 && ', '}
                      {data.alrLengthCorrective > 0 && `${data.alrLengthCorrective.toFixed(1)} ft ALR`}
                    </span>
                  )}
                </td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 py-12">
            <p>{issuesOnly ? 'No segments with issues' : 'No segment data available'}</p>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span>Iowa DOT Section 2317 (Rev. 10/21/2025)</span>
          {isReversed && (
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">Reverse Stationing</span>
          )}
        </div>
        <span>
          MRI: Table {settings.pavementType === 'PCC' 
            ? (settings.projectType === 'PRIMARY' ? '2317.05-3' : '2317.05-4')
            : (settings.projectType === 'PRIMARY' ? '2317.05-5' : '2317.05-6')
          }
          {settings.projectType === 'PRIMARY' && ` (${currentThicknessOption.label})`}
          {' | '}
          ALR: Table {settings.projectType === 'PRIMARY' ? '2317.05-1' : '2317.05-2'}
        </span>
      </div>
    </div>
  );
}